import { Fragment, useEffect, useState } from "react";
import styled from '@mui/system/styled';
import DialogModal from "components/Modal/Dialog";
import { useToasts } from "react-toast-notifications";
import { Controller, useForm } from "react-hook-form";
import UniversalInput from "components/Input/UniversalInput";
import Checkbox from "components/Checkbox";
import SelectDropdown from "../SelectDropdown";
import { getCountryList, getStateList } from "services";

function DictionaryModal({ open, setOpen, name, inputText, onValueChange, createRequest }: any) {
    const { register, unregister, handleSubmit, watch, setValue, setError, control, formState: { errors }, reset } = useForm({
        defaultValues: {
            name: '',
            remote_location: false,
            country: null,
            address: '',
            address_details: '',
            city: null,
            region: '',
            state: null,
            postal_code: ''
        }
    });
    const watchCountry: any = watch('country');
    const watchRemoveLocation = watch('remote_location');

    useEffect(() => {
        if (inputText) setValue('name', inputText)
    }, [inputText, setValue]);

    const { addToast } = useToasts();
    const [reqLoading, setReqLoading] = useState<boolean>(false);

    const humanizeName = () => {
        return name.charAt(0).toUpperCase() + name.slice(1)
            .replace(/^[\s_]+|[\s_]+$/g, '')
            .replace(/[_\s]+/g, ' ')
    };

    const onSubmit = (data: any) => {
        setReqLoading(true);
        createRequest(data).then((res: any) => {
            setReqLoading(false);
            setOpen(false);
            onValueChange(res.data)
            addToast(`${humanizeName()} Added Successfully`, { appearance: 'success', autoDismiss: true });
        }).catch((err: any) => {
            setReqLoading(false);
            if (err?.response?.data?.errors[0].field) {
                addToast(err.response.data.errors[0].message, { appearance: 'error', autoDismiss: true });
                err.response.data.errors.forEach((item: any) => {
                    setError(item.field, { type: 'custom', message: item.message });
                });
            };
        })
    };

    return (
        <DialogModal
            open={open}
            onClose={() => { setOpen(false); reset(); }}
            title={`Add ${humanizeName()}`}
            actionButton={handleSubmit(onSubmit)}
            actionLoading={reqLoading}
            withButtons
            cancelButtonText='CANCEL'
            actionButtonText='SAVE'
            disableAutoFocus
            upperPosition
        >
            <ModalContentContainer>
                <StyledFieldItem>
                    <label>Location Name<sup>*</sup></label>
                    <UniversalInput
                        inputProps={{ maxLength: 250 }}
                        errorText={errors.name ? errors.name.message : ''}
                        {...register('name', { required: 'Location Name is required', maxLength: 250 })}
                    />
                </StyledFieldItem>
                <StyledFieldItem>
                    <Controller
                        name="remote_location"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Checkbox
                                checked={value}
                                onChange={(event) => {
                                    onChange(event.target.checked);
                                    if (event.target.checked) {
                                        unregister(['country', 'address', 'address_details', 'city', 'state', 'region', 'postal_code']);
                                    };
                                }}
                                label={'This location is for remote employees'}
                            />
                        )}
                    />
                </StyledFieldItem>
                {!watchRemoveLocation && <Fragment>
                    <StyledFieldItem style={{ width: '65%' }}>
                        <label>Country<sup>*</sup></label>
                        <Controller
                            name="country"
                            control={control}
                            rules={{ required: 'Country is required' }}
                            render={({ field: { onChange, value } }) => (
                                <SelectDropdown
                                    onChange={(_event: React.SyntheticEvent<Element, Event>, newValue: any) => {
                                        onChange(newValue)
                                    }}
                                    value={value}
                                    inputPlaceholder='Select Country'
                                    loadRemoteData={() => getCountryList(300, 1)}
                                    errorText={errors.country ? errors.country.message : ''}
                                />
                            )}
                        />
                    </StyledFieldItem>
                    <StyledFieldItem style={{ width: '65%' }}>
                        <label>Address Line 1<sup>*</sup></label>
                        <UniversalInput
                            {...register('address', { required: 'Address line 1 is required' })}
                            errorText={errors.address ? errors.address.message : ''}
                            placeholder='Street Address, P.O. Box, Company Name, C/O'
                        />
                    </StyledFieldItem>
                    <StyledFieldItem style={{ width: '65%' }}>
                        <label>Address Line 2</label>
                        <UniversalInput
                            {...register('address_details')}
                            placeholder='Apartment, Suite, Unit, Building, Floor, Etc'
                        />
                    </StyledFieldItem>
                    <div style={{ display: 'flex' }}>
                        <StyledFieldItem style={{ width: '33%' }}>
                            <label>City<sup>*</sup></label>
                            <UniversalInput
                                errorText={errors.city ? errors.city.message : ''}
                                {...register('city', { required: 'City is required' })}
                            />
                        </StyledFieldItem>
                        {watchCountry?.iso === 'US' ? <StyledFieldItem style={{ width: '33%' }}>
                            <label>State / Province / Region<sup>*</sup></label>
                            <Controller
                                name="state"
                                control={control}
                                rules={{ required: "State / Province / Region is required" }}
                                render={({ field: { onChange, value } }) => (
                                    <SelectDropdown
                                        onChange={(_event: React.SyntheticEvent<Element, Event>, newValue: any) => {
                                            onChange(newValue)
                                        }}
                                        value={value}
                                        loadRemoteData={() => getStateList(200, 1)}
                                        inputPlaceholder={'Select State'}
                                        errorText={errors.state ? errors.state.message : ''}
                                    />
                                )}
                            />
                        </StyledFieldItem> : <StyledFieldItem style={{ width: '33%' }}>
                            <label>State / Province / Region<sup>*</sup></label>
                            <UniversalInput
                                {...register('region', { required: 'State / Province / Region is required' })}
                                errorText={errors.region ? errors.region.message : ''}
                            />
                        </StyledFieldItem>}
                        <StyledFieldItem style={{ width: '33%' }}>
                            <label>Zip / Postal Code<sup>*</sup></label>
                            <UniversalInput
                                {...register("postal_code", { required: "Zip / Postal Code is required" })}
                                errorText={errors.postal_code ? errors.postal_code.message : ""}
                            />
                        </StyledFieldItem>
                    </div>
                </Fragment>}
            </ModalContentContainer>
        </DialogModal>
    )

}
export default DictionaryModal;

const ModalContentContainer = styled('div')`
    display: flex;
    flex-direction: column;
    padding-block: 10px;
    max-width: 650px;
    min-width: 500px;
`;

const StyledFieldItem = styled('div')`
    margin-bottom: 20px;
    margin-right: 10px;
    & sup {
        color: #C54343;
    }
    & > label {
        display: inline-block;
        margin-bottom: 6px;
    }
`;